import type { PreferenceFieldWithKey } from '@shared/dream-components';

import type { SubscriberPreference } from '~/interfaces/subscriber_preferences';

import { mapDisplayType } from './mapDisplayType';
import { parseMultipleChoiceValue } from './parseMultipleChoiceValue';

const isNewsletterListPreference = (
  pref: SubscriberPreference,
): pref is SubscriberPreference & { newsletter_list: NonNullable<SubscriberPreference['newsletter_list']> } => {
  return pref.newsletter_list != null;
};

/**
 * Transforms subscriber preferences to the preference fields
 *
 * Subscriber preferences are backed by either a custom field or a newsletter
 * list. Custom-field-backed preferences support all preference types; newsletter-list
 * backed preferences are always boolean toggles.
 *
 * @param subscriberPreferences - Subscriber preferences
 * @returns Preference fields
 */
const transformSubscriberPreferences = (subscriberPreferences: SubscriberPreference[]): PreferenceFieldWithKey[] => {
  return subscriberPreferences.flatMap((pref): PreferenceFieldWithKey[] => {
    const baseKey = pref.id;

    if (isNewsletterListPreference(pref)) {
      return [
        {
          key: baseKey,
          type: 'boolean' as const,
          attrs: {
            data: {
              type: 'newsletter_list' as const,
              name: pref.name || pref.newsletter_list.name,
              list_id: pref.newsletter_list.id,
              value: pref.value as boolean,
              description: pref.description || pref.newsletter_list.description,
              required: pref.required,
            },
          },
        },
      ];
    }

    if (pref.custom_field_id == null) {
      return [];
    }

    const customFieldId = pref.custom_field_id;

    switch (pref.preference_type) {
      case 'boolean':
        return [
          {
            key: baseKey,
            type: 'boolean' as const,
            attrs: {
              data: {
                type: 'custom_field' as const,
                name: pref.name,
                custom_field_id: customFieldId,
                value: pref.value as boolean,
                description: pref.description,
                required: pref.required,
              },
            },
          },
        ];

      case 'text':
        return [
          {
            key: baseKey,
            type: 'text' as const,
            attrs: {
              data: {
                name: pref.name,
                value: pref.value as string | null,
                description: pref.description,
                required: pref.required,
                custom_field_id: customFieldId,
              },
            },
          },
        ];

      case 'dropdown':
        return [
          {
            key: baseKey,
            type: 'dropdown' as const,
            attrs: {
              data: {
                name: pref.name,
                value: pref.value as string | null,
                options: pref.subscriber_preference_options.map((option) => ({
                  id: option.id,
                  value: option.value,
                })),
                description: pref.description,
                required: pref.required,
                custom_field_id: customFieldId,
              },
            },
          },
        ];

      case 'single_choice':
        return [
          {
            key: baseKey,
            type: 'single-choice' as const,
            attrs: {
              data: {
                name: pref.name,
                value: pref.value as string | null,
                options: pref.subscriber_preference_options.map((option) => ({
                  id: option.id,
                  value: option.value,
                })),
                description: pref.description,
                displayType: mapDisplayType(pref.display_type),
                required: pref.required,
                custom_field_id: customFieldId,
              },
            },
          },
        ];

      case 'multiple_choice':
        return [
          {
            key: baseKey,
            type: 'multiple-choice' as const,
            attrs: {
              data: {
                name: pref.name,
                value: parseMultipleChoiceValue(pref.value),
                options: pref.subscriber_preference_options.map((option) => ({
                  id: option.id,
                  value: option.value,
                })),
                description: pref.description,
                displayType: mapDisplayType(pref.display_type),
                required: pref.required,
                custom_field_id: customFieldId,
              },
            },
          },
        ];

      default:
        return [
          {
            key: baseKey,
            type: 'text' as const,
            attrs: {
              data: {
                name: pref.name,
                value: pref.value as string | null,
                description: pref.description,
                required: pref.required,
                custom_field_id: customFieldId,
              },
            },
          },
        ];
    }
  });
};

export default transformSubscriberPreferences;
